import { useCallback, useEffect, useState } from 'react';

import { useOnMissionsChanged } from '../lib/mission/missionsChanged';
import {
  controlErrorMessage,
  controlNeedsPolling,
  type WorkshopControl,
  type WorkshopControlRead,
} from '../lib/mission/workshopControl';
import { supabase } from '../lib/supabase';
import { usePeriodicRead } from './usePeriodicRead';

/** The machine confirms a bascule within ~2 s (its relay polls the bridge): read again at this pace. */
const POLL_MS = 2500;

/**
 * Who holds the browser of the mission's form workshop (`get_workshop_control`), and the two
 * gestures (`take_workshop_control` / `release_workshop_control`). `threadId` null: the form
 * workshop running now, whatever its mission (AI cockpit). Polled while a workshop runs, and at
 * `pollMs` when the caller must see the browser change hands (a gesture on the screen: another
 * mission's workshop may take it while a human acts there).
 */
export function useWorkshopControl(threadId: string | null, options: { pollMs?: number } = {}) {
  const [read, setRead] = useState<WorkshopControlRead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    // Not in the generated Supabase types yet (same escape hatch as useMissionDeliverables).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error: qErr } = await (supabase.rpc as any)('get_workshop_control', { p_thread_id: threadId });
    if (qErr) {
      setError("Impossible de lire qui tient le navigateur de l'atelier.");
    } else {
      setError(null);
      setRead(data as WorkshopControlRead);
    }
    setLoading(false);
  }, [threadId]);

  useEffect(() => {
    void load();
  }, [load]);
  useOnMissionsChanged(() => void load());

  const fast = controlNeedsPolling(read);
  const every = fast ? Math.min(POLL_MS, options.pollMs ?? POLL_MS) : options.pollMs ?? null;
  usePeriodicRead(() => void load(), every);

  const act = useCallback(async (gesture: 'take' | 'release') => {
    const workshopId = read?.workshop?.workshop_id;
    if (!workshopId || busy) return;
    setBusy(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error: qErr } = await (supabase.rpc as any)(
      gesture === 'take' ? 'take_workshop_control' : 'release_workshop_control',
      { p_workshop_id: workshopId },
    );
    if (qErr) {
      setError(controlErrorMessage(qErr.message ?? ''));
    } else {
      setError(null);
      setRead((prev) => (prev ? { ...prev, workshop: data as WorkshopControl } : prev));
    }
    setBusy(false);
  }, [read, busy]);

  return { read, loading, error, busy, take: () => act('take'), release: () => act('release') };
}

export type WorkshopControlHandle = ReturnType<typeof useWorkshopControl>;
